import React from "react";
import { useTranslation } from "react-i18next";
import { useGlobalStateContext } from "../../context/globalState";
import { QualificationsType as SelectedQualificationsType } from "../../context";
import {
  Box,
  Checkbox,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { brown } from "@mui/material/colors";
import { QUALIFICATION_LEVEL_ORDER } from "../helper";

type PropsType = {
  qualifications: {
    qualificationLevelId: number;
    qualificationLevel: string;
    qualificationTypeId: number[];
    trainingId: number;
    trainingName: string;
    trainingType: string;
    trainingTypeId: number;
    validForYears: number | null;
  }[];
  selectedQualifications: SelectedQualificationsType[];
  toggleQualification: (
    trainingName: string,
    trainingType: number,
    trainingId: number,
    qualificationTypeId: number[],
    validYears: number | null,
    qualificationLevel?: string,
    qualificationLevelId?: number | null,
  ) => void;
};

const MethodModulesList: React.FC<PropsType> = ({
  qualifications,
  selectedQualifications,
  toggleQualification,
}) => {
  const { t } = useTranslation();
  const { trainingTypes } = useGlobalStateContext();

  const [expandedLevels, setExpandedLevels] = React.useState<{
    [level: string]: boolean;
  }>({});

  const methodQualifications = qualifications.filter(
    (item) => item.trainingTypeId === trainingTypes.METHOD,
  );

  if (methodQualifications.length === 0) {
    return <div>{t("qualificationMessages.noMethodQualifications")}</div>;
  }

  const hasQualificationLevels = methodQualifications.some(
    (item) =>
      item.qualificationLevelId !== undefined &&
      item.qualificationLevelId !== null,
  );

  if (!hasQualificationLevels) {
    return (
      <List>
        {methodQualifications
          .sort((a, b) => a.trainingName.localeCompare(b.trainingName))
          .map((item) => (
            <ListItem key={item.trainingId}>
              <Checkbox
                checked={
                  !!selectedQualifications.find(
                    (qual) => qual.trainingId === item.trainingId,
                  )
                }
                onChange={() =>
                  toggleQualification(
                    item.trainingName,
                    trainingTypes.METHOD,
                    item.trainingId,
                    item.qualificationTypeId,
                    item.validForYears,
                    item.qualificationLevel,
                    item.qualificationLevelId,
                  )
                }
              />
              <ListItemText primary={item.trainingName} />
            </ListItem>
          ))}
      </List>
    );
  }

  // Group by qualification level. Order the level groups using the shared
  // QUALIFICATION_LEVEL_ORDER constant (defined in helper.ts) so the accordions
  // appear in a predictable order regardless of API response ordering.
  const groupedByLevel: {
    [level: string]: typeof methodQualifications;
  } = {};
  methodQualifications.forEach((item) => {
    const level = item.qualificationLevel || "";
    if (!groupedByLevel[level]) {
      groupedByLevel[level] = [];
    }
    groupedByLevel[level].push(item);
  });

  const orderedLevels = Object.keys(groupedByLevel).sort((a, b) => {
    const aOrder = QUALIFICATION_LEVEL_ORDER[a] ?? Number.MAX_SAFE_INTEGER;
    const bOrder = QUALIFICATION_LEVEL_ORDER[b] ?? Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder;
  });

  const getLevelColor = (levelIndex: number) => {
    const colors = [brown[100], brown[200], brown[300], brown[400], brown[500]];
    return colors[levelIndex % colors.length];
  };

  return (
    <div>
      {orderedLevels.map((level, index) => {
        const items = groupedByLevel[level];
        return (
          <Box key={level} ml={2}>
            <ListItemButton
              onClick={() =>
                setExpandedLevels((prev) => ({
                  ...prev,
                  [level]: !prev[level],
                }))
              }
              sx={{
                backgroundColor: getLevelColor(index),
                borderRadius: 2,
                marginBottom: 1.5,
              }}
            >
              <ListItemText primary={level ? `${level}` : t('qualificationMessages.noLevel')} />
              {expandedLevels[level] ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </ListItemButton>
            <Collapse in={expandedLevels[level]}>
              <List>
                {items
                  .sort((a, b) => a.trainingName.localeCompare(b.trainingName))
                  .map((item) => (
                    <ListItem key={item.trainingId}>
                      <Checkbox
                        checked={
                          !!selectedQualifications.find(
                            (qual) => qual.trainingId === item.trainingId,
                          )
                        }
                        onChange={() =>
                          toggleQualification(
                            item.trainingName,
                            trainingTypes.METHOD,
                            item.trainingId,
                            item.qualificationTypeId,
                            item.validForYears,
                            item.qualificationLevel,
                            item.qualificationLevelId,
                          )
                        }
                      />
                      <ListItemText primary={item.trainingName} />
                    </ListItem>
                  ))}
              </List>
            </Collapse>
          </Box>
        );
      })}
    </div>
  );
};

export default MethodModulesList;
